import {
  EuRelabelCheckIn,
  RelabelCaseBuilderFormData,
  RelabelProductRow,
  CaseProductRow,
  CaseRow,
  RelabelCaseRoutingType,
  RelabelCaseRecord,
  PostRelabelCasesRequestBody
} from "src/types";

export class RelabelCaseBuilderMapper {
  public static initFormData(): RelabelCaseBuilderFormData {
    return {
      purchaseOrderId: "",
      vendorName: "",
      relabelProducts: [],
      cases: []
    };
  }

  public static formDataFromEuRelabelCheckIns(
    purchaseOrderId: string,
    checkIns: EuRelabelCheckIn[],
    existingCases: RelabelCaseRecord[] = []
  ): RelabelCaseBuilderFormData {
    const relabelProducts: RelabelProductRow[] = [];

    for (const checkIn of checkIns) {
      const euRoutingInstructions = checkIn.routingInstructions.filter(
        (ri) => ri.type === "EU_RELABEL" || ri.type === "EU_STANDARD"
      );
      for (const ri of euRoutingInstructions) {
        for (const product of ri.products) {
          const existingProduct = relabelProducts.find((p) => p.id === product.id);
          if (existingProduct) {
            existingProduct.euReceivedQty += product.quantity;
          } else {
            const checkInProduct = checkIn.products.find((p) => p.id === product.id);
            relabelProducts.push({
              id: product.id,
              name: product.name,
              size: checkInProduct?.size,
              barcode: checkInProduct?.barcode ?? "",
              skuName: product.skuName,
              routingType: ri.type as RelabelCaseRoutingType,
              euReceivedQty: product.quantity,
              existingCasedQty: 0,
              distributedQty: 0
            });
          }
        }
      }
    }

    for (const existingCase of existingCases) {
      for (const product of existingCase.products) {
        const row = relabelProducts.find((p) => p.id === product.id);
        if (row) {
          row.existingCasedQty += product.quantity;
          row.distributedQty += product.quantity;
        }
      }
    }

    const vendorName = checkIns[0]?.vendorName ?? "";

    return {
      purchaseOrderId,
      vendorName,
      relabelProducts,
      cases: []
    };
  }

  public static requestBodyFromFormData(formData: RelabelCaseBuilderFormData): PostRelabelCasesRequestBody {
    return {
      ...(formData.purchaseOrderId ? { purchaseOrderId: formData.purchaseOrderId } : {}),
      ...(formData.transferOrderId ? { transferOrderId: formData.transferOrderId } : {}),
      ...(formData.vendorName ? { vendorName: formData.vendorName } : {}),
      cases: formData.cases
        .filter((c) => c.products.some((p) => Number(p.quantity) > 0))
        .map((c) => {
          const activeProducts = c.products.filter((p) => Number(p.quantity) > 0);
          // Derive routing type from the active products in this case
          const derivedRoutingType = activeProducts[0]?.routingType as RelabelCaseRoutingType;

          return {
            caseNumber: c.caseNumber,
            routingType: derivedRoutingType,
            products: activeProducts.map((p) => ({
              id: p.id,
              skuName: p.skuName,
              name: p.name,
              barcode: p.barcode ?? "",
              size: p.size,
              quantity: Number(p.quantity)
            }))
          };
        })
    };
  }

  public static formDataFromTransferOrderEuRelabelCheckIns(
    transferOrderId: string,
    checkIns: EuRelabelCheckIn[],
    existingCases: RelabelCaseRecord[] = []
  ): RelabelCaseBuilderFormData {
    const baseForm = RelabelCaseBuilderMapper.formDataFromEuRelabelCheckIns(transferOrderId, checkIns, existingCases);
    return {
      ...baseForm,
      purchaseOrderId: undefined,
      transferOrderId,
      vendorName: undefined
    };
  }

  public static createEmptyCase(caseNumber: number, relabelProducts: RelabelProductRow[]): CaseRow {
    const products: CaseProductRow[] = relabelProducts.map((p) => ({
      id: p.id,
      skuName: p.skuName,
      name: p.name,
      barcode: p.barcode,
      size: p.size,
      quantity: "0",
      routingType: p.routingType
    }));

    return { caseNumber, routingType: "EMPTY", products };
  }
}
